import type { ApiError } from './axios';
import i18n from './i18n';
import { translateBackendMessage } from './backendMessages';

export { translateBackendMessage };

function isApiError(error: unknown): error is ApiError {
  return error instanceof Error && 'isNetworkError' in error;
}

/**
 * "The backend is momentarily unreachable", as opposed to "your request was wrong"
 * or "the server genuinely broke".
 *
 * The important case is a deploy. During one, the browser never actually sees a
 * 502/503/504: Traefik's synthetic error responses carry no
 * Access-Control-Allow-Origin header (verified against the live stage host), so
 * the browser blocks the response before any JS sees it and axios reports a plain
 * network error with `status === undefined`. That makes `isNetworkError` the arm
 * that fires in production; the status arm only matters same-origin, i.e. local
 * dev, or if a proxy is ever configured to pass CORS headers through on errors.
 */
export function isTransientError(error: unknown): boolean {
  if (!isApiError(error)) return false;
  if (error.isNetworkError) return true;
  return error.status === 502 || error.status === 503 || error.status === 504;
}

/**
 * Turn any thrown error into a sentence a non-technical person can act on.
 *
 * Use this instead of reading `error.message` directly. normalizeError (axios.ts)
 * builds messages for developers -- "Network error: Unable to reach server
 * (GET /api/auth/reset-password/<token>/validate)" -- which are meaningless to a
 * user and embed the request URL, including path parameters that are secrets.
 *
 * Note that normalizeError attaches the response body as `.data` and never as
 * `.response`. Call sites that reached for `.response?.data?.message` were always
 * getting undefined and silently falling through to the developer string, which
 * also made every translated fallback in the app unreachable.
 */
export function toUserMessage(error: unknown, fallback: string): string {
  if (isTransientError(error)) {
    return i18n.t(
      'toast.unreachable',
      "We couldn't reach the server — it may be restarting. Nothing was saved; please try again in a moment.",
    );
  }

  if (isApiError(error)) {
    const payload = error.data as { message?: string; error?: string } | undefined;
    const backendMessage = payload?.message || payload?.error;
    // The API speaks English; the user may not (see backendMessages.ts)
    return backendMessage ? translateBackendMessage(backendMessage) : fallback;
  }

  return fallback;
}
